"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, Phone, Mail, ArrowRight, CircleCheck as CheckCircle2, MapPin, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const faqData = [{
  id: "item-1",
  question: "How quickly can I see local SEO results in Bedford?",
  answer: "Most local businesses in Bedford begin seeing measurable improvements in Google Maps visibility and search rankings within 3 to 6 months. Timelines depend on your starting website authority and local competition. I focus on sustainable, white-hat optimisations that build long-term value rather than unreliable quick fixes."
}, {
  id: "item-2",
  question: "Do you only work with businesses in Bedford?",
  answer: "I work with clients across the UK and internationally, but I specialise in local search strategy for businesses in Bedford and throughout Bedfordshire. My familiarity with local areas like Kempston, Bromham, Biddenham, and Ampthill helps me target authentic local search intent."
}, {
  id: "item-3",
  question: "What makes a freelance SEO consultant different from an SEO agency?",
  answer: "When you hire me, you work directly with a senior SEO specialist rather than being handed off to an account manager or junior executive. You get direct communication, nimble execution, full transparency, and a tailored strategy without agency overheads."
}, {
  id: "item-4",
  question: "How do you measure local SEO success?",
  answer: "I measure success through tangible business metrics that impact your bottom line: Google Map Pack rankings, phone calls, form enquiries, qualified local website traffic, and lead conversions—never vanity metrics."
}, {
  id: "item-5",
  question: "Is cheap SEO worth the money?",
  answer: "Very low-cost SEO packages often rely on automated spam, poor-quality backlinks, and generic templates that can risk algorithmic penalties and damage your search presence. Quality local SEO requires meticulous technical work, bespoke content, and hands-on local optimisation."
}, {
  id: "item-6",
  question: "Can you guarantee Google rankings?",
  answer: "No, and any consultant or agency promising a guaranteed number one position on Google is not being honest. Search algorithms use hundreds of dynamic variables outside anyone's direct control. What I guarantee is transparent, ethical, and rigorous optimisation built for durable search growth."
}, {
  id: "item-7",
  question: "How does local SEO help my Bedford business?",
  answer: "Local SEO positions your business in front of nearby customers searching with high purchase intent (such as 'plumber in Bedford' or 'accountant MK40'). It increases your visibility in Google Maps and local search results, driving more phone calls, enquiries, and foot traffic."
}, {
  id: "item-8",
  question: "Do I need a physical office in Bedford to rank locally?",
  answer: "Having a physical address in Bedford gives you a clear advantage for Google Map Pack rankings. However, if you run a service-area business (SAB) traveling to local clients, I can still help you rank effectively through targeted service areas, local landing pages, and regional authority signals."
}, {
  id: "item-9",
  question: "What is included in Google Business Profile optimisation?",
  answer: "I complete an in-depth optimisation of your Google Business Profile: refining primary and secondary categories, detailing services and products, setting accurate service areas, adding high-resolution photography, establishing a review collection strategy, and managing ongoing updates."
}, {
  id: "item-10",
  question: "Can you help businesses serving multiple Bedfordshire locations?",
  answer: "Yes. If your business serves multiple towns across Bedfordshire—such as Bedford, Luton, Biggleswade, Flitwick, or Sandy—I create bespoke multi-location strategies featuring dedicated, highly relevant landing pages and local schema markup."
}, {
  id: "item-11",
  question: "How much does local SEO cost?",
  answer: "Pricing is custom-built based on your current website health, the competition in your local market, and the scope of work required. I offer one-off comprehensive SEO audits as well as ongoing monthly consultancy. Book a free consultation and I will provide an honest, transparent proposal."
}, {
  id: "item-12",
  question: "What happens after I book a consultation?",
  answer: "After you book a consultation, we will connect for an initial 30-minute discovery call to discuss your business goals, current search challenges, and local competitors in Bedford. I will provide an honest assessment and outline a clear, actionable plan for your local growth."
}];
export default function BedfordFaq() {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredFaqs = faqData.filter(faq => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase()));
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section id="localseobedfordfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      {/* Schema.org FAQPage Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqStructuredData)
    }} />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-12 md:mb-16 max-w-3xl mx-auto">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground rounded-full flex items-center gap-1.5">
            <MapPin className="size-3.5 text-primary" />
            <span>Bedford Local SEO FAQ</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-4">
            Clear Answers for <span className="text-primary">Bedford</span> Business Owners
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Everything you need to know about growing your local search visibility, Google Maps rankings, and customer leads in Bedfordshire—working directly with an independent specialist.
          </p>

          {/* FAQ Search Filter */}
          <div className="mt-8 w-full max-w-md relative">
            <input type="text" placeholder="Search local SEO questions..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full px-4 py-3 pl-10 rounded-xl border border-input bg-card text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-xs transition-colors" />
            <HelpCircle className="absolute left-3 top-3.5 size-4 text-muted-foreground" />
          </div>
        </div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Consultant Sidebar Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-4 bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm lg:sticky lg:top-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="relative size-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20 overflow-hidden">
                <img src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="size-full object-cover" loading="lazy" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-foreground font-serif leading-snug">
                  Yassine Essaadi
                </h3>
                <p className="text-xs text-primary font-medium">
                  Independent SEO Specialist
                </p>
              </div>
            </div>

            <div className="space-y-4 text-sm text-muted-foreground mb-6">
              <p className="leading-relaxed">
                {"\"I work one-on-one with Bedford business owners to deliver tailored local SEO strategies that increase calls, visits, and revenue.\""}
              </p>

              <div className="pt-2 space-y-2">
                <div className="flex items-center gap-2 text-foreground font-medium text-xs">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Direct consultant access (No account managers)</span>
                </div>
                <div className="flex items-center gap-2 text-foreground font-medium text-xs">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Tailored local strategy for Bedford market</span>
                </div>
                <div className="flex items-center gap-2 text-foreground font-medium text-xs">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Transparent monthly performance reporting</span>
                </div>
              </div>
            </div>

            <Separator className="my-6 bg-border" />

            <div className="space-y-3 mb-6">
              <a href="mailto:contact@ye-consultancy.com" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors">
                <Mail className="size-3.5 text-primary" />
                <span>contact@ye-consultancy.com</span>
              </a>
              <a href="tel:+447490984430" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors">
                <Phone className="size-3.5 text-primary" />
                <span>+44 7490984430</span>
              </a>
            </div>

            <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-xs transition-colors rounded-lg h-11">
              <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                <MessageSquare className="size-4" />
                <span>Book a Free SEO Consultation</span>
              </Link>
            </Button>
          </motion.div>

          {/* Accordion Column */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-8 bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm">
            {filteredFaqs.length > 0 ? <Accordion type="single" collapsible className="w-full space-y-3">
                {filteredFaqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border border-border rounded-xl px-4 py-1 transition-colors hover:border-primary/40 bg-background/50">
                    <AccordionTrigger className="text-base md:text-lg font-semibold font-serif text-primary hover:no-underline hover:text-primary/90 py-4 text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion> : <div className="text-center py-12">
                <HelpCircle className="size-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  No matching questions found
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Try adjusting your search terms or reach out directly for a custom answer.
                </p>
                <Button variant="outline" onClick={() => setSearchQuery("")} className="rounded-lg">
                  Clear search
                </Button>
              </div>}

            {/* Quick Contact Box */}
            <div className="mt-8 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 bg-muted/40 p-6 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                  <Sparkles className="size-5 text-accent" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground font-serif">
                    Have a specific question about your Bedford website?
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Get an honest, technical perspective directly from Yassine.
                  </p>
                </div>
              </div>

              <Button asChild variant="outline" size="sm" className="shrink-0 border-primary text-primary hover:bg-primary/10 rounded-lg">
                <Link to="/ContactPage" className="flex items-center gap-1.5">
                  <span>Ask Me Directly</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}